import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import type { ComponentType, FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';
import type { FallbackProps } from 'react-error-boundary';
import { ErrorBoundary } from 'react-error-boundary';
import { addReactError } from '@datadog/browser-rum-react';
import Box from '@mui/material/Box';
import { Typography } from '@mui/material';
import { isRecoverableDomMutationError } from 'lib/datadog';

const RecoverableDomErrorReset: FC<{ resetErrorBoundary: () => void }> = ({
  resetErrorBoundary,
}) => {
  useEffect(() => {
    resetErrorBoundary();
  }, [resetErrorBoundary]);

  return null;
};

const GlobalErrorBoundaryFallback: FC<FallbackProps> = () => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
      <Typography variant="h3" component="h1">
        An unexpected error happened :(
      </Typography>
    </Box>
  );
};

type GlobalErrorBoundaryT = PropsWithChildren & {
  FallbackComponent?: ComponentType<FallbackProps>;
  datadogSource?: string;
}
const GlobalErrorBoundary: FC<GlobalErrorBoundaryT> =
  ({
     children,
     FallbackComponent,
     datadogSource,
   }) => {
    const { reset } = useQueryErrorResetBoundary();

    const DefaultFallback = FallbackComponent ?? GlobalErrorBoundaryFallback;

    return (
      <ErrorBoundary
        onReset={reset}
        onError={(error, errorInfo) => {
          if (isRecoverableDomMutationError(error)) {
            return;
          }

          if (datadogSource) {
            Object.assign(error, {
              dd_context: { source: datadogSource },
            });
          }

          addReactError(error, errorInfo);
        }}
        fallbackRender={({ error, resetErrorBoundary }) => {
          if (isRecoverableDomMutationError(error)) {
            return <RecoverableDomErrorReset resetErrorBoundary={resetErrorBoundary} />;
          }

          return (
            <DefaultFallback
              error={error}
              resetErrorBoundary={resetErrorBoundary}
            />
          );
        }}
      >
        {children}
      </ErrorBoundary>
    );
  };

export default GlobalErrorBoundary;
